import { useState } from "react";
import { Check, Pencil, SkipForward, X } from "lucide-react";
import { cn } from "@/lib/utils";

function Proposal({ update, messageId, onAction }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(Array.isArray(update.value) ? update.value.join("\n") : update.value || "");
  const [busy, setBusy] = useState(false);
  const decided = update.accepted !== null && update.accepted !== undefined;
  const act = async (action, value) => {
    setBusy(true);
    try { await onAction({ message_id: messageId, field: update.field, action, value }); setEditing(false); } finally { setBusy(false); }
  };
  return (
    <div className={cn("rounded-xl border p-3 transition-colors", decided ? (update.accepted ? "border-gold/40 bg-gold/5" : "border-border opacity-60") : "border-border surface")} data-testid={`proposal-${update.field}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow-label !text-[10px] mb-1">{update.label || update.field}</div>
          {!editing && <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid={`proposal-value-${update.field}`}>{Array.isArray(update.value) ? update.value.map((v, i) => <span key={i} className="block">• {v}</span>) : update.value}</p>}
        </div>
        {decided && <span className={cn("shrink-0 text-[10px] font-mono-ls px-2 py-0.5 rounded-full", update.accepted ? "bg-gold text-black" : "border border-border text-muted-foreground")} data-testid={`proposal-status-${update.field}`}>{update.accepted ? "Accepted" : "Skipped"}</span>}
      </div>
      {editing && (
        <div className="mt-2 space-y-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} data-testid={`proposal-edit-input-${update.field}`} className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold/60" />
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={() => act("edit", draft)} className="btn-gold h-8 px-3 text-xs" data-testid={`proposal-edit-save-${update.field}`}><Check className="h-3 w-3" />Save</button>
            <button type="button" onClick={() => setEditing(false)} className="btn-quiet h-8 px-3 text-xs"><X className="h-3 w-3" />Cancel</button>
          </div>
        </div>
      )}
      {!decided && !editing && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => act("accept")} className="btn-gold h-8 px-3 text-xs" data-testid={`proposal-accept-${update.field}`}><Check className="h-3 w-3" />Accept</button>
          <button type="button" disabled={busy} onClick={() => setEditing(true)} className="btn-outline h-8 px-3 text-xs" data-testid={`proposal-edit-${update.field}`}><Pencil className="h-3 w-3" />Edit</button>
          <button type="button" disabled={busy} onClick={() => act("skip")} className="btn-quiet h-8 px-3 text-xs" data-testid={`proposal-skip-${update.field}`}><SkipForward className="h-3 w-3" />Skip</button>
        </div>
      )}
    </div>
  );
}

export function FieldProposals({ message, onAction }) {
  const updates = message.field_updates || [];
  if (!updates.length) return null;
  const pending = updates.filter((u) => u.accepted === null || u.accepted === undefined).length;
  return (
    <div className="mt-3 space-y-2" data-testid={`proposals-${message.id}`}>
      <div className="flex items-center justify-between">
        <span className="eyebrow-label">{updates.length} field{updates.length > 1 ? "s" : ""} proposed</span>
        {pending > 0 && <button type="button" data-testid={`proposals-accept-all-${message.id}`} onClick={() => updates.filter((u) => u.accepted == null).forEach((u) => onAction({ message_id: message.id, field: u.field, action: "accept" }))} className="text-xs gold-text hover:underline">Accept all</button>}
      </div>
      <div className="grid gap-2">
        {updates.map((u) => <Proposal key={u.field} update={u} messageId={message.id} onAction={onAction} />)}
      </div>
    </div>
  );
}
